import React, { useEffect, useRef, useState } from "react";

import { useRootStore } from "store";
import { PRICE_POPUP_PROJECTS } from "common/consts/dashboard";
import { updateFavoriteCustomerList } from "common/customer/getFavoriteCustomer";
import { dateFormat } from "common/dateFormat";
import { Button } from "components/button/Button";
import { Icon } from "components/icon/Icon";
import { LinkPrimaryArrow } from "components/link/Link";
import { Loader } from "components/loader/Loader";
import OverlayLoader from "components/loader/OverlayLoader";
import InfoIconTooltip from "components/tooltip/InfoIconTooltip";
import { Tooltip } from "components/tooltip/Tooltip";
import { ArchiveItem } from "models/ArchiveItem";
import { Banner } from "models/admin/admin";
import Notifications from "views/customers/components/notifications/Notifications";
import ArchivePreview from "../components/archivePreview/ArchivePreview";
import InboxPreview from "../components/inboxPreview/InboxPreview";
import UploadedDocumentsPreview from "../components/uploadedDocumentsPreview/UploadedDocumentsPreview";

import "./Dashboard.scss";

export default function Dashboard() {
  const { user } = useRootStore((state) => state);
  const { dashboard } = useRootStore((state) => state);

  const {
    myCustomers,
    closeCustomer,
    workingCustomer: customer,
  } = useRootStore((state) => state.customers);

  const { fetchNotifications, dismissNotification } = useRootStore(
    (state) => state.notifications
  );

  const { global } = useRootStore((state) => state);

  const { isLoading: isLoadingCustomer, customerNumber } = customer;
  const { threads, uploadedDocuments, banners, archivedFiles, isLoading } =
    dashboard;

  const [archivedFilesToShow, setArchivedFilesToShow] = useState<ArchiveItem[]>(
    []
  );
  const [numberOfFiles, setNumberOfFiles] = useState(0);
  const [showExpand, setShowExpand] = useState(false);
  const [showMinimize, setShowMinimize] = useState(false);
  const [activeBannersList, setActiveBannersList] = useState<Banner[]>([]);
  const [loadingCustomerId, setLoadingCustomerId] = useState(true);

  const isInternalUser = user.userInfo?.type === "Internal";
  const prevIsLoading = useRef(isLoading);

  useEffect(() => {
    const hasCustomers =
      !myCustomers.isLoading && myCustomers.customers.length !== 0;
    if (!hasCustomers) {
      setLoadingCustomerId(false);
    } else {
      setLoadingCustomerId(!global.workingCustomerId);
    }
  }, [global.workingCustomerId, myCustomers.isLoading, myCustomers.customers]);

  useEffect(() => {
    if (!isLoading && prevIsLoading.current) {
      setArchivedFilesToShow(archivedFiles.slice(0, 5));
      setNumberOfFiles(5);
      setShowExpand(true);
      setShowMinimize(false);
    }
    prevIsLoading.current = isLoading;
  }, [isLoading, archivedFiles]);

  useEffect(() => {
    if (banners.length > 0) {
      getActiveBanners();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banners]);

  const handleFetchNotifications = () => {
    fetchNotifications();
  };

  const handleSetNotificationRead = (id: string) => {
    dismissNotification(id);
  };

  const getActiveBanners = () => {
    const hasPricePopupProjects = customer?.activeProjectTypes?.some(
      (projectType) => PRICE_POPUP_PROJECTS.includes(projectType.toUpperCase())
    );
    const activeBanners = banners.filter(function (item) {
      const today = new Date();
      const startDateObject = new Date(item.startDate);
      const endDateObject = new Date(item.endDate);

      return (
        (item.activeProjects ? hasPricePopupProjects : true) &&
        today.getTime() <= endDateObject.getTime() &&
        today.getTime() >= startDateObject.getTime()
      );
    });

    setActiveBannersList(activeBanners);
  };

  const showMoreFiles = () => {
    let sliceNumber = numberOfFiles + 10;
    if (sliceNumber > 50 || sliceNumber >= archivedFiles.length) {
      sliceNumber = 50;
      setShowExpand(false);
      setShowMinimize(true);
    } else {
      setShowExpand(true);
      setShowMinimize(false);
    }
    setArchivedFilesToShow(archivedFiles.slice(0, sliceNumber));
    setNumberOfFiles(sliceNumber);
  };

  const minimizeFiles = () => {
    setArchivedFilesToShow(archivedFiles.slice(0, 5));
    setNumberOfFiles(5);
    setShowExpand(numberOfFiles > 5);
    setShowMinimize(false);
  };

  const setFavoriteCustomer = () => {
    if (user.userInfo) {
      const chosenCustomer = {
        userId: user.userInfo.id,
        customerId: customer.customerNumber
      };
      updateFavoriteCustomerList(chosenCustomer);
    }
  };

  const isCurrentUserSalesPerson = () => {
    if (!customer || !customer.internalUsers || !isInternalUser) {
      return false;
    }

    const userId = user.userInfo?.id;
    return customer.externalUsers.find(
      (externalUser) => externalUser.id === userId && externalUser.isSalePerson
    );
  };

  const getInternalUserComponents = () => {
    const isCustomerClosedInMaconomy = customer.state === "ClosedInMaconomy";

    return {
      externalLinkInfo: "Genvägar till andra tjänster.",
      customerServiceInfo:
        "Här visas vilka digitala tjänster som kunden köper. När du klickar på tjänsten kommer du t ex in till Resultat & Analys.",
      customerInformationInfoText:
        "Här visas information om kunden. Behöver kundinformationen ändras gör du detta på kundkortet i MittLudvig. Om du vill ändra kundens inställningar, klicka på Kundinställningar.",
      notifications: <></>,
      renderDocument: (
        <UploadedDocumentsPreview
          isLoading={isLoading}
          uploadedDocuments={uploadedDocuments}
        />
      ),
      renderCustomerInfo: (
        <>
          <li>
            <span className="informationType">Telefonnummer:</span>{" "}
            <a href={`tel:${customer?.contactInformation?.phone}`}>
              {customer?.contactInformation?.phone}
            </a>
          </li>
          <li>
            <span style={{ display: "block" }}>
              <span className="informationType">Adress:</span>{" "}
              {customer?.contactInformation?.address1}
            </span>
            <span className="indent">
              {customer?.contactInformation?.zipCode}{" "}
              {customer?.contactInformation?.city}{" "}
            </span>
          </li>
        </>
      ),
      customerSettings: (
        <>
          <hr className="footer-border" />
          <LinkPrimaryArrow to={`/customersettings`}>
            Kundinställningar
          </LinkPrimaryArrow>
          {isCustomerClosedInMaconomy && isCurrentUserSalesPerson() && (
            <div className={"mt-relative"}>
              <p>
                <b>Kunden är stängd i Maconomy.</b>
              </p>
              <Button
                size={"sm"}
                onClick={() => closeCustomer(customer.customerNumber)}
              >
                Avsluta kund
              </Button>
            </div>
          )}
        </>
      )
    };
  };

  const getExternalUserComponents = () => {
    return {
      externalLinkInfo: "Genvägar till andra tjänster.",
      customerServiceInfo:
        "Här visas vilka digitala tjänster som du är ansluten till. Genom att klicka på namnet länkas du direkt till tjänsten.",
      customerInformationInfoText:
        "Här ser du dina kunduppgifter; namn kundnummer och organisationsnummer. Om du har flera verksamheter i MittKontor byter du verksamhet genom att klicka på Mina Verksamheter till höger i sidhuvudet.",
      notifications: (
        <>
          <Notifications
            fetchNotifications={handleFetchNotifications}
            setNotificationRead={handleSetNotificationRead}
          />
        </>
      ),
      renderDocument: (
        <ArchivePreview
          showMore={showMoreFiles}
          minimize={minimizeFiles}
          isLoading={isLoading}
          customerId={global.workingCustomerId}
          archivedFiles={archivedFilesToShow}
          showExpand={showExpand}
          showMinimize={showMinimize}
        />
      ),
      renderCustomerInfo: <></>,
      customerSettings: <></>
    };
  };

  const onClickDismiss = (bannerId: string) => {
    const activeBanners = activeBannersList.filter(function (banner) {
      return banner.id !== bannerId;
    });
    setActiveBannersList(activeBanners);
  };

  const {
    notifications,
    externalLinkInfo,
    customerInformationInfoText,
    renderDocument,
    renderCustomerInfo,
    customerSettings
  } = isInternalUser
    ? getInternalUserComponents()
    : getExternalUserComponents();

  const closingWarning = () => {
    if (customer.state === "Closing") {
      const oneDay = 24 * 60 * 60 * 1000;
      const firstDate = new Date();
      const secondDate = new Date(customer.closingAt);

      const daysLeft = Math.round(
        Math.abs((firstDate.getTime() - secondDate.getTime()) / oneDay)
      );
      const daysLeftText =
        daysLeft === 1 ? daysLeft + " dag" : daysLeft + " dagar";

      return (
        <div className="closingWarning">
          <span className="informationType">
            Under avslut - {daysLeftText} kvar
          </span>
        </div>
      );
    }
  };

  const loading =
    isLoadingCustomer ||
    isLoading ||
    loadingCustomerId ||
    myCustomers.isLoading;

  if (!customerNumber && !loading) {
    return <div>Användaren är inte kopplad till någon kund</div>;
  }
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="dashboard">
          {notifications}

          {activeBannersList.map((banner) => (
            <React.Fragment key={banner.id}>
              <div className="popupInfo mb-relative">
                <Icon
                  name="times cursor-pointer"
                  className="popupClose"
                  onClick={() => onClickDismiss(banner?.id)}
                />
                <span>
                  {banner?.message}
                  <a href={banner?.url}>
                    <u>
                      {"   "}
                      {banner?.linkText !== "" ? banner?.linkText : banner?.url}
                    </u>
                  </a>
                </span>
                <div>
                  <small>{dateFormat(new Date(banner?.startDate))}</small>
                </div>
              </div>
            </React.Fragment>
          ))}

          <div className="rowWrapper">
            <div className="leftColumn mb-relative">
              <div className="box mb-relative">
                <InboxPreview isLoading={isLoading} threads={threads} />
              </div>
              <div className={"box"}>{renderDocument}</div>
            </div>

            <div className="rightColumn">
              {!isInternalUser && (
                <div className="box mb-relative">
                  <section aria-label="Genvägar" className="myServices">
                    <h1 className="m-0 d-flex align-items-center">
                      Genvägar{" "}
                      <InfoIconTooltip className="ml-3">
                        {externalLinkInfo}
                      </InfoIconTooltip>
                    </h1>
                    <hr className="header-border" />
                    <div className="dashboardFrame">
                      <div className="services">
                        <Icon name="globe" size="lg" />
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link"
                          href="https://apps.fortnox.se/fs/fs/login.php"
                        >
                          Fortnox
                        </a>
                      </div>
                    </div>
                  </section>
                </div>
              )}

              <div className={"box mb-relative"} aria-label="Kundinformation">
                <section className="customerInformation">
                  {!isInternalUser && (
                    <div className="d-block text-right">
                      <Tooltip
                        content={
                          <div>
                            {global.favoriteCustomer?.customerId ===
                            customer.customerNumber
                              ? `Ta bort som favorit`
                              : `Lägg till som favorit`}
                          </div>
                        }
                      >
                        <Icon
                          name={
                            global.favoriteCustomer?.customerId ===
                            customer.customerNumber
                              ? "star"
                              : "star-o"
                          }
                          className={
                            global.favoriteCustomer?.customerId ===
                            customer.customerNumber
                              ? "favoriteActive"
                              : "favorite"
                          }
                          onClick={setFavoriteCustomer}
                        />
                      </Tooltip>
                    </div>
                  )}
                  <h1 className="m-0 d-flex align-items-center">
                    Kundinformation{" "}
                    <InfoIconTooltip className="ml-3">
                      {customerInformationInfoText}
                    </InfoIconTooltip>
                  </h1>
                  <hr className="header-border" />

                  <OverlayLoader isLoading={isLoadingCustomer}>
                    <div>
                      <ul className="informationBox">
                        {closingWarning()}
                        <li id="customerInformationName">
                          <span className="informationType">Namn:</span>{" "}
                          {!isLoadingCustomer && customer.customerName}
                        </li>
                        <li>
                          <span className="informationType">Kundnummer:</span>{" "}
                          {!isLoadingCustomer && customer.customerNumber}
                        </li>
                        <li>
                          <span className="informationType">
                            Organisationsnummer:
                          </span>{" "}
                          {!isLoadingCustomer && customer.organizationNumber}
                        </li>
                        {!isLoadingCustomer && renderCustomerInfo}
                      </ul>
                      {customerSettings}
                    </div>
                  </OverlayLoader>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
